/**
 * FreeOnDesktopBadge Component
 *
 * Emerald-green clickable badge that shows "Free on Desktop" to encourage
 * users to download the desktop app. Hidden in Electron (user is already on desktop).
 *
 * Navigates to the download page via the 'navigate-to-mode' custom event.
 */

import { memo } from 'react';
import { Monitor } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { isDesktopApp } from '../../utils/apiMode';

interface FreeOnDesktopBadgeProps {
  /** i18n namespace key for the label. Defaults to 'image.freeOnDesktop' */
  i18nKey?: string;
  className?: string;
}

export const FreeOnDesktopBadge = memo(function FreeOnDesktopBadge({
  i18nKey = 'image.freeOnDesktop',
  className = '',
}: FreeOnDesktopBadgeProps) {
  const { t } = useTranslation();

  // Hide when running inside Electron - user is already on desktop
  if (isDesktopApp()) return null;

  return (
    <div
      className={`inline-flex items-center gap-1 cursor-pointer group/desktop ${className}`}
      onClick={() => {
        window.dispatchEvent(new CustomEvent('navigate-to-mode', { detail: 'download' }));
      }}
    >
      <Monitor className="w-3 h-3 text-emerald-400/60 group-hover/desktop:text-emerald-400 transition-colors" />
      <span className="text-[10px] text-emerald-400/60 font-medium group-hover/desktop:text-emerald-400 transition-colors">
        {t(i18nKey, { defaultValue: 'Free on Desktop' })}
      </span>
    </div>
  );
});

export default FreeOnDesktopBadge;
